import { useMemo } from "react";
import {
  Link,
  useParams,
  useNavigate,
  useSearchParams,
} from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Skeleton } from "@/components/ui/skeleton";
import { QueryResult } from "@/components/QueryResult";
import { CompanyForm } from "@/components/forms/CompanyForm";
import {
  useCompany,
  useCreateCompany,
  useUpdateCompany,
  type CompanyInsert,
  type CompanyUpdate,
} from "@/hooks/useCompanies";
import { useProjects } from "@/hooks/useProjects";
import { Badge } from "@/components/ui/badge";
import { format } from "date-fns";
import { useSetCurrentEntity } from "@/contexts/RightSidebarContext";
import { usePageHeader } from "@/hooks/usePageHeader";

export default function CompanyDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isNew = id === "new";

  const {
    data: company,
    isLoading,
    isError,
    error,
  } = useCompany(isNew ? undefined : id);
  const { data: projects } = useProjects();
  useSetCurrentEntity("company", isNew ? undefined : id, company?.name);
  const createCompany = useCreateCompany();
  const updateCompany = useUpdateCompany();

  const companyProjects = projects?.filter((p) => p.customer_id === id) ?? [];

  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get("tab");
  const currentTab = tabParam === "projects" ? "projects" : "details";
  const handleTabChange = (value: string) => {
    setSearchParams({ tab: value }, { replace: true });
  };

  const headerTabs = useMemo(
    () => [
      { value: "details", label: "Details" },
      { value: "projects", label: `Projects (${companyProjects.length})` },
    ],
    [companyProjects.length],
  );

  usePageHeader({
    title: isNew ? "New Company" : company?.name,
    tabs: isNew ? undefined : headerTabs,
    currentTab: isNew ? undefined : currentTab,
    onTabChange: isNew ? undefined : handleTabChange,
  });

  const handleSubmit = async (data: CompanyInsert | CompanyUpdate) => {
    if (isNew) {
      await createCompany.mutateAsync(data as CompanyInsert);
      navigate("/companies");
    } else if (id) {
      await updateCompany.mutateAsync({ id, updates: data as CompanyUpdate });
      navigate("/companies");
    }
  };

  if (isLoading && !isNew) {
    return (
      <div className="space-y-6 animate-fade-in">
        <div className="flex items-center gap-4">
          <Skeleton className="h-10 w-10" />
          <Skeleton className="h-8 w-48" />
        </div>
        <Card>
          <CardContent className="p-6">
            <div className="space-y-4">
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-24 w-full" />
              <Skeleton className="h-10 w-1/2" />
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!isNew && !company) {
    return (
      <QueryResult
        isError={isError}
        error={error}
        data={company}
        entityName="company"
        backTo="/companies"
        backLabel="Back to Companies"
      >
        {null}
      </QueryResult>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate("/companies")}
        >
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <h1 className="text-2xl font-semibold">
          {isNew ? (
            "New Company"
          ) : (
            <>
              {company?.number && (
                <span className="text-muted-foreground font-mono mr-2">
                  {company.number}
                </span>
              )}
              {company?.name || "Company"}
            </>
          )}
        </h1>
      </div>

      {isNew ? (
        <Card>
          <CardContent className="p-6">
            <CompanyForm
              onSubmit={handleSubmit}
              onCancel={() => navigate("/companies")}
              isLoading={createCompany.isPending}
            />
          </CardContent>
        </Card>
      ) : (
        <Tabs
          value={currentTab}
          onValueChange={handleTabChange}
          className="space-y-4"
        >
          <TabsList>
            <TabsTrigger value="details">Details</TabsTrigger>
            <TabsTrigger value="projects">
              Projects ({companyProjects.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="details">
            <Card>
              <CardContent className="p-6">
                <CompanyForm
                  company={company}
                  onSubmit={handleSubmit}
                  onCancel={() => navigate("/companies")}
                  isLoading={updateCompany.isPending}
                />
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="projects">
            <Card>
              <CardHeader>
                <CardTitle>Projects</CardTitle>
              </CardHeader>
              <CardContent>
                {companyProjects.length === 0 ? (
                  <p className="text-muted-foreground text-center py-8">
                    No projects linked to this company.
                  </p>
                ) : (
                  <div className="space-y-2">
                    {companyProjects.map((project) => (
                      <Link
                        key={project.id}
                        to={`/projects/${project.id}`}
                        className="flex items-center justify-between p-3 rounded-lg border text-inherit no-underline hover:bg-muted/50 transition-colors"
                      >
                        <div>
                          <p className="font-medium">{project.name}</p>
                          <p className="text-xs text-muted-foreground">
                            {project.event_start_date &&
                              format(
                                new Date(project.event_start_date),
                                "MMM d, yyyy",
                              )}
                            {project.event_end_date &&
                              ` - ${format(new Date(project.event_end_date), "MMM d, yyyy")}`}
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          {project.status && (
                            <Badge variant="outline" className="capitalize">
                              {project.status.replace("_", " ")}
                            </Badge>
                          )}
                          {project.types?.map((type) => (
                            <Badge
                              key={type}
                              variant="secondary"
                              className="capitalize"
                            >
                              {type}
                            </Badge>
                          ))}
                        </div>
                      </Link>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      )}
    </div>
  );
}
